const RACING_SPORT_IDS = new Set([10, 11, 20, 21, 30]);
const BASEBALL_SPORT_ID = 40;
const FOOTBALL3D_SPORT_ID = 50;

function nonEmptyString(x: unknown): string | null {
  const s = typeof x === 'string' ? x : x != null ? String(x) : '';
  const t = s.trim();
  return t.length > 0 ? t : null;
}

export type LiveWindow = {
  startSec: number;
  liveUntilSec: number;
};

export function liveWindowFromResults(payload: any): LiveWindow | null {
  if (!payload || typeof payload !== 'object') return null;
  const startSec = payload?.event_timestamp != null ? Number(payload.event_timestamp) : null;
  if (startSec == null || !Number.isFinite(startSec) || startSec <= 0) return null;

  const endTs = payload?.end_timestamp != null ? Number(payload.end_timestamp) : null;
  if (endTs != null && Number.isFinite(endTs) && endTs > startSec) {
    return { startSec, liveUntilSec: endTs };
  }
  const dur = payload?.duration != null ? Number(payload.duration) : null;
  if (dur != null && Number.isFinite(dur) && dur > 0) {
    return { startSec, liveUntilSec: startSec + dur };
  }
  return null;
}

/**
 * End of live window in seconds (Unix) from an event-like object (upcoming item or results).
 * Used to decide if an event is "not ended yet" for next-event selection.
 */
export function liveUntilSecFromEventLike(ev: any): number | null {
  if (!ev || typeof ev !== 'object') return null;
  const endTs = ev?.end_timestamp != null ? Number(ev.end_timestamp) : null;
  if (endTs != null && Number.isFinite(endTs) && endTs > 0) return endTs;
  const startTs = ev?.event_timestamp != null ? Number(ev.event_timestamp) : null;
  const dur = ev?.duration != null ? Number(ev.duration) : null;
  if (startTs != null && Number.isFinite(startTs) && startTs > 0 && dur != null && Number.isFinite(dur) && dur > 0) {
    return startTs + dur;
  }
  return null;
}

/**
 * Video readiness gate for showing LIVE in UI.
 * - Racing: requires vod.hls_url
 * - Baseball: requires vod.urls[0]
 * - Football3D: requires vod.urls and, when video_id_sequence exists, requires exact length match.
 */
export function isVideoReadyFromResults(payload: any, sportId: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const sid = Number(sportId);
  const vod = payload?.vod;

  if (RACING_SPORT_IDS.has(sid)) {
    return nonEmptyString(vod?.hls_url) != null;
  }

  if (sid === BASEBALL_SPORT_ID || sid === FOOTBALL3D_SPORT_ID) {
    const urlsRaw = Array.isArray(vod?.urls) ? vod.urls : [];
    const urls = urlsRaw.map((u: any) => nonEmptyString(u)).filter((s: string | null) => s != null) as string[];
    if (urls.length === 0) return false;

    if (sid === FOOTBALL3D_SPORT_ID) {
      const seqRaw = Array.isArray(payload?.video_id_sequence) ? payload.video_id_sequence : [];
      const seq = seqRaw.map((v: any) => nonEmptyString(v)).filter((s: string | null) => s != null) as string[];
      if (seqRaw.length > 0) {
        // Enforce alignment: treat mismatch as not-ready.
        if (seq.length === 0) return false;
        if (urls.length !== seq.length) return false;
        if (urls.some((u) => !u || u.trim().length === 0)) return false;
      }
    }

    return true;
  }

  return false;
}

